import { Link, Outlet } from "@tanstack/react-router";
import { Badge, Button } from "@empite/ui-components";
import { useAdminSignOut, useSignedInOperator } from "../admin-hooks";
import { can, isPlatformStaff, tenantLabel } from "./guards";
import { FailuresNavLink } from "./failures";
import { FAILURES_READ } from "./failure-log";
import { ReasonPromptProvider } from "./reason-prompt";
import { BillingNoticeBanner } from "./billing-notices";

/**
 * The shell every admin screen renders inside.
 *
 * The header states WHICH TENANT this session is confined to, who the operator
 * is, what roles they hold and when their session expires, all of the time. In
 * a back office that is not decoration: an operator who cannot see which
 * account they are signed in as is one shared laptop away from acting as
 * somebody else, an operator who cannot see which tenant they are in is one
 * click away from suspending the wrong company's customer, and an expiry that
 * arrives as a surprise 401 arrives in the middle of a suspension.
 */
export function Layout() {
  const { ready, signedIn, session } = useSignedInOperator();
  const signOut = useAdminSignOut();
  const operator = session?.operator;

  return (
    <div className="esm-app">
      <nav className="esm-nav">
        <Link to="/" className="esm-nav__brand">
          Empite Admin
        </Link>
        <div className="esm-nav__links">
          {signedIn && operator ? (
            <>
              {/* EVERY entry below is gated, and the four that used to be
                  ungated were not universal — they were unreviewed. A back
                  office serving more than one tenant cannot afford that: the
                  Admin API guards each of these routes with the permission
                  named here and AUDITS the refusal, so an ungated link is a
                  denial written against an operator who only clicked what the
                  nav offered them. Hiding is still a courtesy and never a
                  control; the WHERE clause and the Guard are the control. */}
              {can(operator, "applications:read") ? (
                <Link to="/applications">Applications</Link>
              ) : null}
              {can(operator, "users:read") ? (
                <Link to="/users">Users</Link>
              ) : null}
              {can(operator, "providers:read") ? (
                <Link to="/providers">Providers</Link>
              ) : null}
              {/* Templates is the PLATFORM's provider catalogue — one
                  definition every tenant may instantiate — so it is
                  platform-only in the nav for the reason Models below is: the
                  writes underneath it are model_catalogue:write, which no
                  tenant-confined subject can hold, and offering the link to an
                  operator whose own providers live one entry to the left
                  invites an audited 403. */}
              {isPlatformStaff(operator) && can(operator, "providers:read") ? (
                <Link to="/provider-templates">Templates</Link>
              ) : null}
              {/* Models is the SHARED catalogue — one list every tenant routes
                  against — so it is platform-only in the nav even though a
                  tenant operator holds the providers:read that lists it. What
                  a tenant may do with those models is set on its own routing
                  profiles; offering it the platform's catalogue as a
                  destination invites an edit that model_catalogue:write, which
                  no tenant-confined subject can hold, refuses. */}
              {isPlatformStaff(operator) && can(operator, "providers:read") ? (
                <Link to="/models">Models</Link>
              ) : null}
              {can(operator, "providers:read") ? (
                <Link to="/routing-profiles">Routing</Link>
              ) : null}
              {/* Companies and Tiers are the platform's own two screens over
                  the tenancy schema, and they are gated on their OWN
                  permissions rather than on isPlatformStaff: both
                  tenants:read_all and tenants:manage are in the Admin API's
                  platform-only set, so AllowedInScope already refuses them to
                  a tenant-confined subject and `can` is reading the union the
                  server computed under that same rule. Two links because they
                  are two grants — reading who is about to run out is support
                  work, re-pricing what they ran out of is not. */}
              {can(operator, "tenants:read_all") ? (
                <Link to="/tenants">Companies</Link>
              ) : null}
              {can(operator, "tenants:manage") ? (
                <Link to="/tiers">Tiers</Link>
              ) : null}
              {/* The TENANT's own two, and the mirror of the pair above.
                  Billing is gated on tenant_billing:read, which only the two
                  tenant seats hold — platform staff belong to no company, so
                  there is no "my bill" for them and the link is not drawn.
                  Plans is gated on the catalogue reads instead of on a tenant
                  test, because the inheritance view is readable by platform
                  staff too: it is just that every row in it is theirs and
                  nothing offers a Clone. */}
              {can(operator, "tenant_billing:read") ? (
                <Link to="/billing">Billing</Link>
              ) : null}
              {can(operator, "packages:read") || can(operator, "pricing:read") ? (
                <Link to="/plans">Plans</Link>
              ) : null}
              {can(operator, "pricing:read") ? (
                <Link to="/pricing">Pricing</Link>
              ) : null}
              {/* The same grant as Pricing, and a second link rather than a
                  tab inside it: the review queue is a job with an inbox, and
                  an operator checking whether last night's fetch left anything
                  to decide should not have to open the price history first.
                  pricing:read is the right gate — the queue is readable by
                  anybody who may read the rates, and the decide buttons are
                  drawn off pricing:write inside the screen. */}
              {can(operator, "pricing:read") ? (
                <Link to="/pricing/proposals">Price review</Link>
              ) : null}
              {can(operator, "packages:read") ? (
                <Link
                  to="/packages"
                  search={{ application_id: undefined, status: undefined }}
                >
                  Packages
                </Link>
              ) : null}
              {/* Gated on the same rule as Packages above: the Admin API
                  guards every /analytics route with analytics:read and audits
                  the refusal, and support holds no analytics grant at all. */}
              {can(operator, "analytics:read") ? (
                <Link
                  to="/analytics"
                  search={{
                    since: undefined,
                    until: undefined,
                    days: undefined,
                  }}
                >
                  Analytics
                </Link>
              ) : null}
              {/* Gated on BOTH grants, unlike Analytics above. The Admin API
                  takes wallets:read at the door of /analytics/revenue rather
                  than over its fields — a revenue report with the money
                  withheld is a window object and a list of currency codes —
                  so an operator holding only analytics:read gets a 403 and an
                  audited denial from this link every time, which is a link
                  that should not be there. */}
              {can(operator, "analytics:read") &&
              can(operator, "wallets:read") ? (
                <Link
                  to="/analytics/revenue"
                  search={{
                    since: undefined,
                    until: undefined,
                    days: undefined,
                  }}
                >
                  Revenue
                </Link>
              ) : null}
              {/* Gated on analytics:read ALONE, like Analytics above and
                  unlike Revenue: the Admin API takes wallets:read over the
                  three COST fields of this route rather than at its door, so
                  an operator holding only analytics:read gets the verdicts,
                  the token dimensions and the request counts — a complete and
                  useful document — with the money absent. A second grant on
                  the link would hide a page they are allowed to read. */}
              {can(operator, "analytics:read") ? (
                <Link
                  to="/analytics/drift"
                  search={{
                    from: undefined,
                    to: undefined,
                    provider: undefined,
                    drifted: undefined,
                  }}
                >
                  Drift
                </Link>
              ) : null}
              {can(operator, "wallets:read") ? (
                <Link
                  to="/wallets"
                  search={{ q: undefined, status: undefined }}
                >
                  Wallets
                </Link>
              ) : null}
              {/* Alerts is gated on wallets:read like its two neighbours,
                  and on purpose NOT on a grant of its own: a runout alert
                  discloses exactly what /wallets does — is this customer
                  funded — at one moment in the past, so a second permission
                  would be a second copy of one authorization rule. The link
                  carries no count, unlike Failures: the badge there exists
                  because a fault nobody is looking at is an outage, where an
                  open alert is a customer the sweep has already emailed. */}
              {can(operator, "wallets:read") ? (
                <Link
                  to="/alerts"
                  search={{ kind: undefined, severity: undefined, state: undefined }}
                >
                  Alerts
                </Link>
              ) : null}
              {/* Payments reads /top-ups, which the Admin API guards with
                  wallets:read — the same grant, because a top-up IS a movement
                  on the wallet this link's neighbour shows. */}
              {can(operator, "wallets:read") ? (
                <Link to="/payments" search={{ status: undefined }}>
                  Payments
                </Link>
              ) : null}
              {/* audit:read is withheld from both tenant roles on purpose:
                  admin.admin_actions is one of the three stores M1.2-M1.4 left
                  unscoped, and the grant follows the WHERE clause. */}
              {can(operator, "audit:read") ? (
                <Link
                  to="/audit"
                  search={{
                    outcome: undefined,
                    resource_type: undefined,
                    action: undefined,
                    since: undefined,
                    until: undefined,
                  }}
                >
                  Audit
                </Link>
              ) : null}
              {/* Gated like Packages and Analytics, and it owns its own
                  gating: the link carries a COUNT, so it has a read behind it
                  that must not be issued for an operator the Admin API would
                  refuse — a link that 403s on every screen also writes an
                  audited denial on every screen. billing_ops is the role
                  without failures:read; its money-shaped failures are already
                  on /wallets behind wallets:read.

                  The badge is why this entry is a component rather than a
                  Link: an incident should be visible from the nav without the
                  console being opened, and "nothing is failing" and "the log
                  could not be read" must not draw the same. */}
              <FailuresNavLink mayRead={can(operator, FAILURES_READ)} />
              {/* The TENANT's own seats, gated on team:manage — which only
                  tenant_owner and super_admin hold. A tenant_member is refused
                  both reads behind this link with a 403, so the link is not
                  drawn for them. It is a separate grant from admins:manage on
                  purpose: the two routes here filter on the request's
                  TenantScope in their WHERE clause, and the platform operator
                  directory still does not. */}
              {can(operator, "team:manage") ? <Link to="/team">Team</Link> : null}
              {/* The tenant's integration credential — what its own backend
                  presents to /v1/tenant. Its own grant, tenant_api_keys:manage,
                  held by tenant_owner and super_admin: a key is a standing
                  licence to bill any of the tenant's customers, which is not
                  the same trust as adding a colleague. */}
              {can(operator, "tenant_api_keys:manage") ? <Link to="/api-keys">API keys</Link> : null}
              <span className="esm-operator">
                {/* WHICH TENANT, before who. The console renders identically
                    whichever tenant it is pointed at, and every act in it —
                    a suspension, a status change, a package migration — lands
                    on a real company. "Platform" is the widest possible reach
                    rather than the absence of a tenant, so it is drawn in the
                    loudest tone the badge has. */}
                <Badge tone={isPlatformStaff(operator) ? "warning" : "neutral"}>
                  {tenantLabel(operator)}
                </Badge>
                <strong>{operator.display_name || operator.email}</strong>
                {operator.roles.map((role) => (
                  <Badge key={role}>{role}</Badge>
                ))}
                {session ? (
                  <span className="esm-operator__expiry">
                    session until{" "}
                    {new Date(session.expires_at).toLocaleTimeString()}
                  </span>
                ) : null}
              </span>
              {/* Signing out needs no reason code: DELETE /admin/v1/session
                  sits outside the Admin API's RequireReason subtree, because
                  demanding a justification for leaving is how you get
                  operators who stay signed in. */}
              <Button
                variant="ghost"
                busy={signOut.isPending}
                onClick={() => signOut.mutate()}
              >
                Sign out
              </Button>
            </>
          ) : ready ? (
            <>
              <Link to="/sign-in" search={{ next: undefined }}>
                Sign in
              </Link>
              {/* Self-signup registers a NEW tenant. It is offered here rather
                  than only on the sign-in page because a stranger who lands on
                  the console root should not have to guess that the way in is
                  behind the way back. */}
              <Link to="/sign-up">Register a company</Link>
            </>
          ) : null}
        </div>
      </nav>
      <main className="esm-main">
        {/* The provider wraps every screen, so a mutating button anywhere in
            the console has a reason prompt to go through and no screen has to
            remember to mount one. */}
        <ReasonPromptProvider>
          {/* Inside the provider because Dismiss is a reasoned write. Gated
              on the Billing link's own grant, so platform staff never issue
              the audited read. */}
          {signedIn && operator ? (
            <BillingNoticeBanner mayRead={can(operator, "tenant_billing:read")} />
          ) : null}
          <Outlet />
        </ReasonPromptProvider>
      </main>
    </div>
  );
}
